import i18n, { type InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import es from './locales/es.json';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  normalizeLanguage,
  type SupportedLanguage,
} from '../../shared/constants/languages.ts';
import { setAppLanguage } from './locale.ts';

/**
 * i18next setup (English + Spanish, automatic browser detection).
 *
 * The app has no manual language switcher, so detection is deliberately
 * narrow: read `navigator.language` once and never persist an override. A
 * returning visitor whose browser language changed — or who is testing on a
 * borrowed device — should see the language their browser reports today, not
 * whatever was cached on a previous visit.
 *
 * Resources are bundled directly rather than fetched, since the whole
 * translation set is small; this also means the very first render already has
 * the right strings, with no loading flash.
 */
/**
 * `initImmediate` isn't in this version's type declarations, though it is a
 * real runtime option — it defers init to a timer tick even for synchronous,
 * bundled resources. Turning it off means `i18n.t()` works immediately after
 * `.init()` returns, which api/client.ts's `describeStatus` relies on.
 */
const options: InitOptions & { initImmediate?: boolean } = {
  resources: {
    en: { translation: en },
    es: { translation: es },
  },
  supportedLngs: [...SUPPORTED_LANGUAGES],
  fallbackLng: DEFAULT_LANGUAGE,
  // 'es-MX', 'es-419', etc. all map to the 'es' bundle instead of falling
  // back to English.
  nonExplicitSupportedLngs: true,
  detection: {
    order: ['navigator'],
    caches: [],
  },
  interpolation: {
    // React already escapes rendered strings.
    escapeValue: false,
  },
  returnNull: false,
  initImmediate: false,
};

/**
 * Keeps `<html lang>` in step with whichever language is actually showing.
 *
 * `index.html` ships with a static `lang="en"` — fine before i18next has run,
 * but wrong the moment detection picks Spanish. Screen readers, spellcheck
 * and browser-translation prompts all key off this attribute, so it has to
 * track every resolution, not just the first: `languageChanged` covers both
 * the initial detection and any later `changeLanguage()` call — and so does
 * the date locale, so dates follow a language picked in the app. The listener
 * is registered before `.init()` runs so it also catches the very first
 * resolution, which — with `initImmediate: false` — happens synchronously
 * inside `.init()` itself.
 */
i18n.on('languageChanged', (language) => {
  setAppLanguage(language);
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
});

void i18n.use(LanguageDetector).use(initReactI18next).init(options);

/**
 * The language the app is showing right now, as a tag the API accepts.
 *
 * Sign-up sends this so the account's starter rooms, tasks, payment methods
 * and quote terms are written in the language the person filling the form is
 * reading — those are database rows, not translated strings, so the choice has
 * to be made at creation time or not at all.
 */
export function activeLanguage(): SupportedLanguage {
  return normalizeLanguage(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LANGUAGE;
}

export default i18n;
